import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subscription, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AudioService } from './audio.service';
import { SignalRService, CAMERA_STATUS } from './signalr.service';

/** Status of a single Jetson device from API */
export interface JetsonDeviceStatus {
    hardwareId: string;
    deviceName: string;
    online: boolean;
    lastSeen: Date | null;
    lastIp: string | null;
    tenantId: number;
}

/** Aggregated status for UI display */
export interface JetsonStatus {
    online: boolean;
    lastSeen: Date | null;
    tenantId: number;
    deviceCount: number;
    onlineCount: number;
    devices: JetsonDeviceStatus[];
    anyReconnecting: boolean;
}

export type ConnectionState = 'online' | 'offline' | 'unknown' | 'partial' | 'reconnecting';

/**
 * Service that tracks Jetson device connectivity status.
 *
 * Strategy:
 *   1. HTTP fetch on start (source of truth: LastSeenAt vs timeout en API)
 *   2. SignalR for real-time online/offline transitions
 *   3. Safety re-fetch: periódico + al reconectar SignalR + al volver a la pestaña
 *      (evita UI clavada en "Desconectado" si se perdió un push DeviceStatusChanged;
 *       el heartbeat MQTT actualiza DB pero no siempre emite SignalR)
 */
@Injectable({
    providedIn: 'root'
})
export class JetsonStatusService implements OnDestroy {
    private readonly API_URL = `${environment.apiBaseUrl}/api/v1/DeviceManagement/status`;
    /** Revalidación HTTP: el heartbeat solo escribe LastSeenAt; sin esto la UI puede quedar stale. */
    private readonly STATUS_REFRESH_MS = 20_000;

    private statusSubject = new BehaviorSubject<JetsonStatus | null>(null);
    private signalRSubscription?: Subscription;
    private heartbeatSubscription?: Subscription;
    private connectionStateSubscription?: Subscription;
    private isStarted = false;

    /** Map cameraId → está en reconnecting. Se limpia si no llega heartbeat en 12s. */
    private cameraReconnectingMap = new Map<string, boolean>();
    private cameraHeartbeatTimestamps = new Map<string, number>();
    private reconnectingCleanupInterval?: ReturnType<typeof setInterval>;
    private statusRefreshInterval?: ReturnType<typeof setInterval>;
    private readonly onVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
            this.fetchStatus().subscribe();
        }
    };

    /** Observable del estado actual de la Jetson */
    public status$ = this.statusSubject.asObservable();

    constructor(
        private http: HttpClient,
        private audioService: AudioService,
        private signalRService: SignalRService
    ) { }

    ngOnDestroy(): void {
        this.stop();
    }

    /**
     * Inicia el monitoreo del estado de la Jetson.
     * Fetch HTTP + SignalR + revalidación de seguridad.
     * Seguro de llamar múltiples veces - solo inicia una vez.
     */
    async startMonitoring(): Promise<void> {
        if (this.isStarted) return;
        this.isStarted = true;

        // 1. Start SignalR connection (shared, idempotent)
        await this.signalRService.start();

        // 2. Initial HTTP fetch to get current status
        this.fetchStatus().subscribe();

        // 3. Subscribe to real-time device status changes via SignalR
        this.signalRSubscription = this.signalRService.deviceStatusChanged$.subscribe(
            (deviceUpdate) => this.handleDeviceStatusChange(deviceUpdate)
        );

        // 4. Subscribe to camera heartbeats to track reconnecting state
        this.heartbeatSubscription = this.signalRService.cameraHeartbeat$.subscribe(
            (hb) => this.handleCameraHeartbeat(hb.cameraId, hb.status)
        );

        // 5. Cleanup obsolete reconnecting flags every 6s (TTL: 12s)
        this.reconnectingCleanupInterval = setInterval(() => {
            const now = Date.now();
            let changed = false;
            this.cameraHeartbeatTimestamps.forEach((ts, cid) => {
                if (now - ts > 12000) {
                    this.cameraReconnectingMap.delete(cid);
                    this.cameraHeartbeatTimestamps.delete(cid);
                    changed = true;
                }
            });
            if (changed) {
                this.emitReconnectingState();
            }
        }, 6000);

        // 6. Safety: re-fetch periódico (heartbeat no siempre empuja SignalR online)
        this.statusRefreshInterval = setInterval(() => {
            this.fetchStatus().subscribe();
        }, this.STATUS_REFRESH_MS);

        // 7. Tras reconexión SignalR, alinear con API (pudo haberse perdido DeviceStatusChanged)
        this.connectionStateSubscription = this.signalRService.connectionState$.subscribe((state) => {
            if (state === 'connected') {
                this.fetchStatus().subscribe();
            }
        });

        // 8. Al volver a la pestaña, revalidar
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', this.onVisibilityChange);
        }
    }

    /**
     * Detiene el monitoreo. La conexión SignalR NO se cierra
     * (es compartida con otros servicios).
     */
    stop(): void {
        if (this.signalRSubscription) {
            this.signalRSubscription.unsubscribe();
            this.signalRSubscription = undefined;
        }
        if (this.heartbeatSubscription) {
            this.heartbeatSubscription.unsubscribe();
            this.heartbeatSubscription = undefined;
        }
        if (this.connectionStateSubscription) {
            this.connectionStateSubscription.unsubscribe();
            this.connectionStateSubscription = undefined;
        }
        if (this.reconnectingCleanupInterval !== undefined) {
            clearInterval(this.reconnectingCleanupInterval);
            this.reconnectingCleanupInterval = undefined;
        }
        if (this.statusRefreshInterval !== undefined) {
            clearInterval(this.statusRefreshInterval);
            this.statusRefreshInterval = undefined;
        }
        if (typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this.onVisibilityChange);
        }
        this.isStarted = false;
    }

    /**
     * Backward-compatible method for components that call startPolling().
     * Now starts SignalR-based monitoring instead of polling.
     */
    startPolling(): void {
        this.startMonitoring();
    }

    /**
     * Backward-compatible method for components that call stopPolling().
     */
    stopPolling(): void {
        this.stop();
    }

    /**
     * Obtiene el estado de conexión simplificado.
     * Jerarquía: reconnecting > partial > online > offline > unknown.
     */
    getConnectionState(status: JetsonStatus | null): ConnectionState {
        if (!status || status.deviceCount === 0) return 'unknown';
        if (status.onlineCount === 0) return 'offline';
        if (status.anyReconnecting) return 'reconnecting';
        if (status.onlineCount === status.deviceCount) return 'online';
        return 'partial';
    }

    /**
     * Formatea el tiempo transcurrido desde el último heartbeat.
     */
    getLastSeenText(status: JetsonStatus | null): string {
        if (!status?.lastSeen) {
            return 'Nunca conectado';
        }

        const lastSeen = new Date(status.lastSeen);
        const now = new Date();
        const diffMs = now.getTime() - lastSeen.getTime();
        const diffSeconds = Math.floor(diffMs / 1000);
        const diffMinutes = Math.floor(diffSeconds / 60);
        const diffHours = Math.floor(diffMinutes / 60);

        if (diffSeconds < 10) {
            return 'Hace unos segundos';
        } else if (diffSeconds < 60) {
            return `Hace ${diffSeconds} segundos`;
        } else if (diffMinutes < 60) {
            return `Hace ${diffMinutes} minuto${diffMinutes > 1 ? 's' : ''}`;
        } else if (diffHours < 24) {
            return `Hace ${diffHours} hora${diffHours > 1 ? 's' : ''}`;
        } else {
            return lastSeen.toLocaleString('es-AR');
        }
    }

    /**
     * Handles a device status change event from SignalR.
     * Updates the aggregated status and plays audio feedback.
     */
    private handleDeviceStatusChange(update: JetsonDeviceStatus): void {
        const currentStatus = this.statusSubject.getValue();
        if (!currentStatus) {
            // If we don't have a status yet, do a full fetch
            this.fetchStatus().subscribe();
            return;
        }

        // Find and update the device in our local list
        const deviceIndex = currentStatus.devices.findIndex(
            d => d.hardwareId === update.hardwareId
        );

        let updatedDevices: JetsonDeviceStatus[];
        if (deviceIndex >= 0) {
            // Update existing device
            const previousDevice = currentStatus.devices[deviceIndex];
            updatedDevices = [...currentStatus.devices];
            updatedDevices[deviceIndex] = update;

            // Play audio on state transition
            if (previousDevice.online && !update.online) {
                console.log('[JetsonStatus] 📴 Device went offline:', update.deviceName);
                this.audioService.playDeviceDisconnected();
            } else if (!previousDevice.online && update.online) {
                console.log('[JetsonStatus] 📶 Device came online:', update.deviceName);
                this.audioService.playDeviceConnected();
            }
        } else {
            // New device we haven't seen - add it
            updatedDevices = [...currentStatus.devices, update];
        }

        // Re-aggregate and emit
        const newStatus = this.aggregateStatus(updatedDevices);
        this.statusSubject.next(newStatus);
    }

    private fetchStatus(): Observable<JetsonStatus | null> {
        return this.http.get<JetsonDeviceStatus[]>(this.API_URL).pipe(
            map(devices => this.aggregateStatus(devices)),
            tap(status => {
                this.statusSubject.next(status);
            }),
            catchError(error => {
                console.warn('[JetsonStatus] Error fetching status:', error);
                this.statusSubject.next(null);
                return of(null);
            })
        );
    }

    /**
     * Aggregates multiple device statuses into a single status for the UI.
     */
    private aggregateStatus(devices: JetsonDeviceStatus[]): JetsonStatus {
        const onlineDevices = devices.filter(d => d.online);
        const mostRecentDevice = devices
            .filter(d => d.lastSeen)
            .sort((a, b) => new Date(b.lastSeen!).getTime() - new Date(a.lastSeen!).getTime())[0];
        const anyReconnecting = [...this.cameraReconnectingMap.values()].some(v => v === true);

        return {
            online: onlineDevices.length > 0,
            lastSeen: mostRecentDevice?.lastSeen ?? null,
            tenantId: devices[0]?.tenantId ?? 0,
            deviceCount: devices.length,
            onlineCount: onlineDevices.length,
            devices: devices,
            anyReconnecting
        };
    }

    /**
     * Procesa un heartbeat por cámara y actualiza el mapa de reconnecting.
     */
    private handleCameraHeartbeat(cameraId: string, status: number): void {
        this.cameraHeartbeatTimestamps.set(cameraId, Date.now());
        if (status === CAMERA_STATUS.Reconnecting) {
            this.cameraReconnectingMap.set(cameraId, true);
        } else if (status === CAMERA_STATUS.Running) {
            this.cameraReconnectingMap.set(cameraId, false);
        }
        this.emitReconnectingState();
    }

    /**
     * Emite el estado actual actualizado con anyReconnecting recalculado.
     */
    private emitReconnectingState(): void {
        const current = this.statusSubject.getValue();
        if (!current) return;
        const anyReconnecting = [...this.cameraReconnectingMap.values()].some(v => v === true);
        this.statusSubject.next({ ...current, anyReconnecting });
    }
}
